import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { REVIEWS } from "../../data";
import { useLang } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initials = (name) =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

export const Reviews = () => {
  const { t } = useLang();
  const r = t.reviews;
  const [live, setLive] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/google-reviews`)
      .then((res) => {
        if (res.data && res.data.available && Array.isArray(res.data.reviews) && res.data.reviews.length) {
          setLive(res.data);
        }
      })
      .catch(() => {});
  }, []);

  const list = live ? live.reviews : REVIEWS;
  const ratingText = live && live.rating ? Number(live.rating).toFixed(1) : "5.0";
  const totalText = live && live.total ? `${live.total}+` : "222+";
  return (
    <section id="reviews" data-testid="reviews-section" className="relative py-24 sm:py-32 bg-[#060c1f]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-8 items-center mb-14">
          <Reveal className="lg:col-span-7">
            <SectionLabel num="[ 05 ]">{r.label}</SectionLabel>
            <h2 className="font-head font-extrabold uppercase tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.9]">
              {r.title1}
              <span className="text-gold-grad">{r.title2}</span>
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.1}>
            <div className="rounded-2xl card-hairline p-7 flex items-center gap-6" data-testid="review-badge">
              <div className="text-center">
                <div className="font-head font-extrabold text-6xl text-white leading-none">{ratingText}</div>
                <div className="flex justify-center mt-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={15} className="fill-amber-400 text-amber-400" />
                  ))}
                </div>
              </div>
              <div className="h-16 w-px bg-white/10" />
              <div>
                <div className="font-head font-bold text-2xl text-white">{totalText}</div>
                <div className="text-sm text-slate-400">{r.googleReviews}</div>
                <div className="mt-1 font-mono-x text-[11px] uppercase tracking-widest text-emerald-400">{r.recommend}</div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {list.map((rev, i) => (
            <motion.div
              key={rev.name + i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="mb-5 break-inside-avoid rounded-2xl card-hairline p-6 hover:border-amber-400/30 transition-colors"
              data-testid={`review-card-${i}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-amber-400/15 border border-amber-400/30 flex items-center justify-center font-head font-bold text-amber-400 text-sm">
                    {initials(rev.name)}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white leading-tight">{rev.name}</div>
                    <div className="text-[11px] text-slate-500">{rev.when}</div>
                  </div>
                </div>
                <Quote size={18} className="text-white/15" />
              </div>
              <div className="flex mb-3">
                {[...Array(5)].map((_, k) => (
                  <Star key={k} size={13} className="fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">{rev.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
